/**
 * TableBuilder — Grid-picker modal for inserting tables into the editor.
 *
 * Hover over cells to preview the size, click to insert.
 * Max 10×10. First row becomes <thead>.
 */
import React, { useState, useCallback } from 'react';

const MAX_ROWS = 10;
const MAX_COLS = 10;
const PREVIEW_ROWS = 8;
const PREVIEW_COLS = 8;

function generateTableHtml(rows, cols) {
    const id = `tbl-${Date.now().toString(36)}`;
    const ths = Array.from({ length: cols }, (_, i) =>
        `<th contenteditable="true">Header ${i + 1}</th>`
    ).join('');

    const tds = Array.from({ length: cols }, () =>
        `<td contenteditable="true"></td>`
    ).join('');

    const bodyRows = Array.from({ length: rows - 1 }, () =>
        `<tr>${tds}</tr>`
    ).join('');

    return (
        `<table class="finlytics-table" data-table-id="${id}">` +
        `<thead><tr>${ths}</tr></thead>` +
        `<tbody>${bodyRows}</tbody>` +
        `</table><p><br></p>`
    );
}

export default function TableBuilder({ open, onClose, onInsert }) {
    const [hoverRow, setHoverRow] = useState(0);
    const [hoverCol, setHoverCol] = useState(0);
    const [customRows, setCustomRows] = useState('');
    const [customCols, setCustomCols] = useState('');

    const handleCellClick = useCallback(() => {
        if (hoverRow < 1 || hoverCol < 1) return;
        const html = generateTableHtml(hoverRow + 1, hoverCol + 1); // +1 because 0-indexed
        onInsert?.(html);
        onClose?.();
    }, [hoverRow, hoverCol, onInsert, onClose]);

    const handleCustomInsert = useCallback(() => {
        const r = Math.min(parseInt(customRows, 10) || 2, MAX_ROWS);
        const c = Math.min(parseInt(customCols, 10) || 2, MAX_COLS);
        if (r < 1 || c < 1) return;
        const html = generateTableHtml(r, c);
        onInsert?.(html);
        onClose?.();
    }, [customRows, customCols, onInsert, onClose]);

    if (!open) return null;

    return (
        <div className="tbl-builder-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="tbl-builder" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tbl-builder-header">
                    <span className="tbl-builder-title">Insert Table</span>
                    <button type="button" className="tbl-builder-close" onClick={onClose}>✕</button>
                </div>

                {/* Grid picker */}
                <div className="tbl-builder-label">
                    {hoverRow >= 0 && hoverCol >= 0
                        ? `${hoverRow + 1} × ${hoverCol + 1} table`
                        : 'Select table size'}
                </div>
                <div
                    className="tbl-builder-grid"
                    onMouseLeave={() => { setHoverRow(-1); setHoverCol(-1); }}
                >
                    {Array.from({ length: PREVIEW_ROWS }, (_, r) => (
                        <div key={r} className="tbl-builder-row">
                            {Array.from({ length: PREVIEW_COLS }, (_, c) => (
                                <div
                                    key={c}
                                    className={`tbl-builder-cell ${r <= hoverRow && c <= hoverCol ? 'active' : ''}`}
                                    onMouseEnter={() => { setHoverRow(r); setHoverCol(c); }}
                                    onClick={handleCellClick}
                                />
                            ))}
                        </div>
                    ))}
                </div>

                {/* Custom size */}
                <div className="tbl-builder-custom">
                    <span className="tbl-builder-custom-label">Custom:</span>
                    <input
                        type="number"
                        min="1"
                        max={MAX_ROWS}
                        placeholder="Rows"
                        value={customRows}
                        onChange={(e) => setCustomRows(e.target.value)}
                        className="tbl-builder-input"
                    />
                    <span>×</span>
                    <input
                        type="number"
                        min="1"
                        max={MAX_COLS}
                        placeholder="Cols"
                        value={customCols}
                        onChange={(e) => setCustomCols(e.target.value)}
                        className="tbl-builder-input"
                    />
                    <button
                        type="button"
                        className="tbl-builder-insert-btn"
                        onClick={handleCustomInsert}
                        disabled={!customRows || !customCols}
                    >
                        Insert
                    </button>
                </div>
            </div>
        </div>
    );
}
